/**
 * Meal Planning, Recipe Details, and Cook Mode product telemetry.
 * Wraps trackUiEvent with stable segmentation fields.
 */

import { trackUiEvent } from './uiAnalytics.js'

const FEATURE_AREA_BY_PREFIX = [
  ['meal_plan_', 'meal_planning'],
  ['planner_assistant_', 'meal_planning'],
  ['recipe_detail_', 'recipe_details'],
  ['recipe_pantry_', 'recipe_details'],
  ['cook_', 'cook_mode'],
]

function inferFeatureArea(event) {
  for (const [prefix, area] of FEATURE_AREA_BY_PREFIX) {
    if (event.startsWith(prefix)) return area
  }
  return 'other'
}

/**
 * @param {string} event
 * @param {Record<string, string|number|boolean|null|undefined>} [properties]
 */
export function trackCookingEvent(event, properties = {}) {
  trackUiEvent(event, {
    feature_area: inferFeatureArea(event),
    ...properties,
  })
}

export function trackRecipeDetailOpened(recipeId, source = 'recipe_detail') {
  trackCookingEvent('recipe_detail_opened', {
    recipe_id: recipeId,
    source,
  })
}

export function trackRecipeDetailAction(recipeId, action, source = 'recipe_detail', extra = {}) {
  trackCookingEvent('recipe_detail_action_clicked', {
    recipe_id: recipeId,
    action,
    source,
    ...extra,
  })
}

export function trackCookModeStarted({
  recipeId,
  sessionId,
  planItemId = null,
  source = 'recipe_detail',
} = {}) {
  trackCookingEvent('cook_mode_started', {
    recipe_id: recipeId,
    session_id: sessionId,
    plan_item_id: planItemId,
    source,
    cook_origin: planItemId ? 'planned' : 'unplanned',
  })
}

export function trackCookModeCompleted({
  recipeId,
  sessionId,
  planItemId = null,
  elapsedSeconds = null,
  source = 'cook_mode',
} = {}) {
  trackCookingEvent('cook_mode_completed', {
    recipe_id: recipeId,
    session_id: sessionId,
    plan_item_id: planItemId,
    elapsed_seconds: elapsedSeconds,
    source,
    cook_origin: planItemId ? 'planned' : 'unplanned',
  })
}

export function trackCookModeAbandoned({
  recipeId,
  sessionId,
  planItemId = null,
  lastStepIndex = null,
  elapsedSeconds = null,
  source = 'cook_mode',
} = {}) {
  trackCookingEvent('cook_mode_abandoned', {
    recipe_id: recipeId,
    session_id: sessionId,
    plan_item_id: planItemId,
    last_step_index: lastStepIndex,
    elapsed_seconds: elapsedSeconds,
    source,
    cook_origin: planItemId ? 'planned' : 'unplanned',
  })
}

/**
 * Light telemetry for plan slot moves (including cross-week). Diagnostic only.
 * @param {Record<string, string|number|boolean|null|undefined>} props
 */
export function trackMealPlanItemMoved(props = {}) {
  trackCookingEvent('meal_plan_item_moved', {
    source: 'meal_plan',
    ...props,
  })
}
